// SubscriptionContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserTier } from '../types';
import { SubscriptionContextType } from '../types/subscription';

// Add type declaration for import.meta.env
declare global {
  interface ImportMeta {
    env: {
      VITE_WORKER_URL: string;
      [key: string]: string;
    };
  }
}

// Default ad frequency for free users (2 = show an ad after every 2 messages)
export const DEFAULT_AD_FREQUENCY = 3;

const defaultContextValue: SubscriptionContextType = {
  tier: 'free',
  adFrequency: DEFAULT_AD_FREQUENCY,
  isLoading: false,
  error: null,
  checkSubscriptionStatus: async () => {
    console.warn('SubscriptionContext not initialized');
  },
  forceRefresh: async () => {
    console.warn('SubscriptionContext not initialized');
  }
};

const SubscriptionContext = createContext<SubscriptionContextType>(defaultContextValue);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tier, setTier] = useState<UserTier>('free');
  const [adFrequency, setAdFrequency] = useState<number>(DEFAULT_AD_FREQUENCY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCheck, setLastCheck] = useState(0);
  const MIN_CHECK_INTERVAL = 30000; // 30 seconds - more frequent checks for better security

  const checkSubscriptionStatus = async (force = false) => {
    const now = Date.now();
    if (!force && now - lastCheck < MIN_CHECK_INTERVAL) {
      return; // Prevent frequent rechecks
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('sessionToken');
      if (!token) {
        // No token available - user not logged in yet
        // This is not an error, just skip the check
        setIsLoading(false);
        return;
      }

      // PHASE 5 SECURITY: Call NEW endpoint that returns server-authoritative tier info
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/tier-info`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // If unauthorized, clear the stored tier info but don't throw error
        if (response.status === 401) {
          console.log('Session expired or invalid, skipping tier check');
          setError(null);
        } else {
          throw new Error('Failed to check subscription');
        }
        return;
      }
      
      const data = await response.json();
      
      // Use server's tier determination
      setTier(data.tier);
      setAdFrequency(data.features.adFrequency);
      
      setLastCheck(now);
      setError(null);
    } catch (error) {
      console.error('Subscription check error:', error);
      setError(error instanceof Error ? error.message : 'Subscription check failed');
    } finally {
      setIsLoading(false);
    }
  };

  const forceRefresh = async () => {
    await checkSubscriptionStatus(true);
  };

  // Initial check on mount
  useEffect(() => {
    checkSubscriptionStatus(true);
  }, []);

  const value = {
    tier,
    adFrequency,
    isLoading,
    error,
    checkSubscriptionStatus,
    forceRefresh
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  return context;
};
